import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { X } from "lucide-react";

const FORMATS = [
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.UPC_A,
  Html5QrcodeSupportedFormats.UPC_E,
  Html5QrcodeSupportedFormats.CODE_128,
];

export const BarcodeScanner = ({ onDetected, onClose }) => {
  const [error, setError] = useState(null);
  const scannerRef = useRef(null);
  const firedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const scanner = new Html5Qrcode("barcode-reader", {
      formatsToSupport: FORMATS,
      verbose: false,
    });
    scannerRef.current = scanner;
    scanner
      .start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 260, height: 140 } },
        (text) => {
          if (firedRef.current) return;
          firedRef.current = true;
          onDetected(text);
        },
        () => {} // per-frame decode misses — ignore
      )
      .then(() => {
        // StrictMode double-mount: camera finished starting after unmount.
        if (cancelled && scanner.isScanning) {
          scanner.stop().catch(() => {});
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setError(
          String(e).toLowerCase().includes("permission")
            ? "Camera permission denied — allow camera access and try again."
            : "Couldn't start the camera on this device."
        );
      });
    return () => {
      cancelled = true;
      // stop() throws synchronously if the scanner never started — guard it.
      try {
        if (scanner.isScanning) {
          scanner.stop().then(() => {
            try { scanner.clear(); } catch {}
          }).catch(() => {});
        } else {
          try { scanner.clear(); } catch {}
        }
      } catch {}
    };
  }, [onDetected]);

  return (
    <div className="space-y-3" data-testid="barcode-scanner">
      <div className="flex items-center justify-between">
        <div className="overline">Point your camera at the barcode</div>
        <button onClick={onClose} data-testid="barcode-scanner-close" className="text-zinc-400 hover:text-white" aria-label="Close scanner">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div id="barcode-reader" className="rounded-sm overflow-hidden border border-obsidian-600 min-h-[200px] bg-black" />
      {error && (
        <p className="text-sm text-red-400" data-testid="barcode-scanner-error">{error}</p>
      )}
    </div>
  );
};

export default BarcodeScanner;
